import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, ShieldCheck, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, assetUrl, formatApiError } from "@/lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    address: "", city: "", state: "", pincode: "", coupon_code: "", notes: "",
  });
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
      } catch (e) {
        toast.error("Product not found");
      }
    })();
  }, [id]);

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const payload = {
        ...form,
        items: [{
          product_id: product.id, name: product.name, price: product.price,
          quantity: qty, image_url: product.image_url,
        }],
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order placed! Total ₹${Number(data.total).toLocaleString("en-IN")}`);
      setOpenCheckout(false);
      setForm({ customer_name: "", customer_email: "", customer_phone: "", address: "", city: "", state: "", pincode: "", coupon_code: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Order failed");
    } finally {
      setPlacing(false);
    }
  };

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center text-stone-600" data-testid="product-loading">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[color:var(--ats-bg)]" data-testid="product-detail">
      <header className="sticky top-0 z-40 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-stone-700 hover:text-stone-900" data-testid="back-link">
            <ArrowLeft className="h-4 w-4" /> Back to collection
          </Link>
          <div className="font-serif-display text-xl">ATSHREE</div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="aspect-square md:aspect-[4/5] rounded-md overflow-hidden bg-white border border-stone-200">
          <img src={assetUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="overline">{product.category}</div>
          <h1 className="font-serif-display text-3xl lg:text-4xl mt-2 leading-tight">{product.name}</h1>
          <div className="flex items-baseline gap-3 mt-4">
            <span className="font-mono-accent text-2xl">₹{Number(product.price).toLocaleString("en-IN")}</span>
            {product.compare_at_price && (
              <span className="font-mono-accent text-sm text-stone-500 line-through">
                ₹{Number(product.compare_at_price).toLocaleString("en-IN")}
              </span>
            )}
          </div>
          <p className="mt-6 text-stone-700 leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-stone-300 rounded-md h-12 overflow-hidden">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-4 h-full hover:bg-stone-100"
                data-testid="qty-decrease"
              >−</button>
              <span className="px-4 font-mono-accent" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty(Math.min(product.stock || 99, qty + 1))}
                className="px-4 h-full hover:bg-stone-100"
                data-testid="qty-increase"
              >+</button>
            </div>
            <Dialog open={openCheckout} onOpenChange={setOpenCheckout}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[color:var(--ats-crimson)] hover:bg-[#5a0016] text-white h-12 rounded-md px-8 flex-1"
                  disabled={product.stock <= 0}
                  data-testid="buy-now-btn"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" /> Buy Now
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="checkout-dialog">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-2xl">Complete your order</DialogTitle>
                </DialogHeader>
                <form onSubmit={placeOrder} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input required placeholder="Full name" value={form.customer_name}
                      onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="checkout-name" />
                    <Input required type="tel" placeholder="Phone" value={form.customer_phone}
                      onChange={(e) => setForm({...form, customer_phone: e.target.value})} data-testid="checkout-phone" />
                  </div>
                  <Input required type="email" placeholder="Email" value={form.customer_email}
                    onChange={(e) => setForm({...form, customer_email: e.target.value})} data-testid="checkout-email" />
                  <Textarea required placeholder="Address" value={form.address}
                    onChange={(e) => setForm({...form, address: e.target.value})} data-testid="checkout-address" />
                  <div className="grid grid-cols-3 gap-3">
                    <Input required placeholder="City" value={form.city}
                      onChange={(e) => setForm({...form, city: e.target.value})} data-testid="checkout-city" />
                    <Input required placeholder="State" value={form.state}
                      onChange={(e) => setForm({...form, state: e.target.value})} data-testid="checkout-state" />
                    <Input required placeholder="Pincode" value={form.pincode}
                      onChange={(e) => setForm({...form, pincode: e.target.value})} data-testid="checkout-pincode" />
                  </div>
                  <Input placeholder="Coupon code (optional)" value={form.coupon_code}
                    onChange={(e) => setForm({...form, coupon_code: e.target.value})} data-testid="checkout-coupon" />
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-sm">
                      <div className="text-stone-500">Total</div>
                      <div className="font-mono-accent text-xl">
                        ₹{Number(product.price * qty).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <Button type="submit" disabled={placing}
                      className="bg-[color:var(--ats-emerald)] hover:bg-emerald-800 h-11 text-white"
                      data-testid="checkout-submit">
                      {placing ? "Placing…" : "Place Order (COD)"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-white rounded-md border border-stone-200">
              <ShieldCheck className="h-5 w-5 text-[color:var(--ats-emerald)] mt-0.5" />
              <div>
                <div className="font-semibold text-sm">Lifetime warranty</div>
                <div className="text-xs text-stone-600 mt-0.5">On plating & stone-setting</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white rounded-md border border-stone-200">
              <Truck className="h-5 w-5 text-[color:var(--ats-gold-dark)] mt-0.5" />
              <div>
                <div className="font-semibold text-sm">Free shipping</div>
                <div className="text-xs text-stone-600 mt-0.5">On orders above ₹1,999</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
